import React from "react";
import { CircleDot, CheckCircle2, XCircle, Clock, Train } from "lucide-react";
import { Tooltip } from "./Tooltip";

export const PipelineStatus = ({ pipeline }) => {
	if (!pipeline) {
		return (
			<Tooltip content="No pipeline found">
				<CircleDot className="w-4 h-4 text-yellow-400 monokai:text-monokai-yellow dracula:text-dracula-yellow nord:text-nord-orange" />
			</Tooltip>
		);
	}

	// Check if pipeline is in merge train
	const isInMergeTrain = pipeline.mergeRequestEventType === "MERGE_TRAIN";
	const mergeTrainPrefix = isInMergeTrain ? "Merge train: " : "";

	// Get the appropriate icon based on status and merge train
	const getIcon = (status, animate = false) => {
		const iconProps = {
			className: `w-4 h-4 ${getColorClass(status)} ${animate ? "animate-pulse" : ""}`,
		};

		if (isInMergeTrain) {
			return <Train {...iconProps} />;
		}

		switch (status) {
			case "running":
				return <Clock {...iconProps} />;
			case "success":
				return <CheckCircle2 {...iconProps} />;
			case "failed":
				return <XCircle {...iconProps} />;
			default:
				return <CircleDot {...iconProps} />;
		}
	};

	// Get color class based on status
	const getColorClass = (status) => {
		switch (status) {
			case "success":
				return "text-green-500 monokai:text-monokai-green dracula:text-dracula-green nord:text-nord-green";
			case "failed":
				return "text-red-500 monokai:text-monokai-red dracula:text-dracula-red nord:text-nord-red";
			case "running":
			case "progress":
				return "text-blue-500 monokai:text-monokai-blue dracula:text-dracula-cyan nord:text-nord-brightBlue";
			default:
				return "text-yellow-400 monokai:text-monokai-yellow dracula:text-dracula-yellow nord:text-nord-orange";
		}
	};

	// Get badge background color class based on status
	const getBadgeClass = (status) => {
		switch (status) {
			case "success":
				return "bg-green-500 monokai:bg-monokai-green dracula:bg-dracula-green nord:bg-nord-green";
			case "failed":
				return "bg-red-500 monokai:bg-monokai-red dracula:bg-dracula-red nord:bg-nord-red";
			default:
				return "bg-blue-500 monokai:bg-monokai-blue dracula:bg-dracula-cyan nord:bg-nord-brightBlue";
		}
	};

	// Pipeline is currently running
	if (pipeline.active) {
		return (
			<Tooltip content={`${mergeTrainPrefix}Pipeline is running`}>
				<div className="relative">
					{getIcon("running")}
					<span
						className={`absolute -bottom-1 -right-1 text-[10px] font-medium ${getBadgeClass("running")} text-white rounded-full w-3 h-3 flex items-center justify-center`}
					>
						1
					</span>
				</div>
			</Tooltip>
		);
	}

	const totalJobs = pipeline.totalJobs || 0;
	const failedJobs = pipeline.failedJobs || 0;
	const passedJobs = pipeline.passedJobs || 0;
	if (pipeline.status === "success") {
		return (
			<Tooltip
				content={`${mergeTrainPrefix}All checks passed (${totalJobs} total)`}
			>
				<div className="relative">
					{getIcon("success")}
					<span
						className={`absolute -bottom-1 -right-1 text-[10px] font-medium ${getBadgeClass("success")} text-white rounded-full w-3 h-3 flex items-center justify-center`}
					>
						✓
					</span>
				</div>
			</Tooltip>
		);
	}

	if (failedJobs > 0) {
		// Failed checks
		return (
			<Tooltip
				content={`${mergeTrainPrefix}${failedJobs} of ${totalJobs} checks failed`}
			>
				<div className="relative">
					{getIcon("failed")}
					<span
						className={`absolute -bottom-1 -right-1 text-[10px] font-medium ${getBadgeClass("failed")} text-white rounded-full w-3 h-3 flex items-center justify-center`}
					>
						{failedJobs}
					</span>
				</div>
			</Tooltip>
		);
	}

	// Checks in progress
	const remainingJobs = totalJobs - passedJobs;
	return (
		<Tooltip
			content={`${mergeTrainPrefix}Running checks: ${passedJobs} of ${totalJobs} completed`}
		>
			<div className="relative">
				{getIcon("progress", true)}
				<span
					className={`absolute -bottom-1 -right-1 text-[10px] font-medium ${getBadgeClass("progress")} text-white rounded-full w-3 h-3 flex items-center justify-center`}
				>
					{remainingJobs}
				</span>
			</div>
		</Tooltip>
	);
};
